import type { Psdata } from '@vue-storefront/prestashop-api';

interface UseProductAddToCartOptions {
  // Run before the normal flow; return/resolve true to stop there (e.g. redirect to the
  // full product page instead of adding to cart). The product page doesn't need this.
  beforeAddToCart?: () => boolean | Promise<boolean>;
  // Run after a successful add to cart, before the cart sidebar opens (e.g. refetch product data).
  afterAddToCart?: () => unknown | Promise<unknown>;
}

export const useProductAddToCart = (
  product: ComputedRef<Pick<Psdata, 'id_product' | 'id_product_attribute' | 'quantity' | 'allow_backorder' | 'availability'> | null>,
  selectedQuantity: Ref<number>,
  options: UseProductAddToCartOptions = {},
) => {
  const generalStore = useGeneralStore();
  const { execute: addToCart, loading } = useAddToCart();

  const showPreorderModal = ref(false);
  const showBackorderModal = ref(false);

  const normalizedAvailability = computed(() => product.value?.availability?.toLowerCase());

  // Check if the product is a preorder (backorder with zero stock)
  const isPreorder = computed(() => normalizedAvailability.value === 'backorder');

  // Check if selected quantity exceeds available stock (partial backorder)
  const isBackorder = computed(() => {
    const availableQty = Number(product.value?.quantity || 0);
    return normalizedAvailability.value === 'available' &&
           availableQty > 0 &&
           product.value?.allow_backorder &&
           selectedQuantity.value > availableQty;
  });

  async function addProductToCart() {
    if (!product.value) return;
    await addToCart({
      ...product.value,
      id_product: product.value.id_product,
      id_product_attribute: product.value.id_product_attribute,
      qty: selectedQuantity.value,
    });
    await options.afterAddToCart?.();
    if (!generalStore.isCartSidebarOpen) {
      generalStore.toggleCartSidebar();
    }
  }

  async function doAddToCart() {
    if (!product.value) return;

    if (await options.beforeAddToCart?.()) {
      return;
    }

    // Check if this is a preorder - show confirmation modal first
    if (isPreorder.value) {
      showPreorderModal.value = true;
      return;
    }

    // Check if this is a backorder - show confirmation modal first
    if (isBackorder.value) {
      showBackorderModal.value = true;
      return;
    }

    // For regular products, proceed directly to cart
    await addProductToCart();
  }

  async function confirmPreorder() {
    // Close the modal and proceed with adding to cart
    showPreorderModal.value = false;
    await addProductToCart();
  }

  async function confirmBackorder() {
    // Close the modal and proceed with adding to cart
    showBackorderModal.value = false;
    await addProductToCart();
  }

  return {
    loading,
    showPreorderModal,
    showBackorderModal,
    isPreorder,
    isBackorder,
    doAddToCart,
    confirmPreorder,
    confirmBackorder,
    addProductToCart,
  };
};
